<script lang="ts">
  // Reusable "comments" entry point for activity cards/pages that don't already have a richer
  // dialog (like ExerciseDialog) showing comments inline. Opens a small comments-only dialog --
  // just the thread, nothing else -- wired the same way ExerciseDialog.svelte wires
  // CommentThreadPanel for a given activity path.
  import { getContext } from 'svelte';
  import * as Dialog from '$lib/components/ui/dialog';
  import { Button } from '$lib/components/ui/button';
  import { cn } from '@/lib/utils';
  import CommentThreadPanel from './CommentThreadPanel.svelte';
  import { getActivityComments, getActivityCommentCounts, postActivityComment, markActivityCommentsRead } from './api';
  import type { MyFitnessStores } from '@/MyFitness/stores';

  // Optional -- present whenever a Today/Calendar/ActivitiesDash-style ancestor set it up (every
  // current caller), absent for a hypothetical future selfFetch-only embed. Used on read to clear
  // the *shared* commentCounts store (so every badge for this path updates, not just this one).
  const myFitness = getContext<MyFitnessStores | undefined>('myFitness');

  export let activityPath: string;
  export let activityName: string | null = null;
  // Counts are normally batch-loaded by a parent list (Today/Calendar/ActivitiesDash all fetch
  // once for their whole list via MyFitnessStores.loadCommentCounts and pass the result down) --
  // these may be transiently undefined until that batch resolves, which is fine, the badge just
  // stays blank until then. Only set selfFetch when there's no such list (e.g. the legacy
  // single-activity detail page) -- otherwise every card would fire its own request instead of
  // sharing the one batch call.
  export let total: number | undefined = undefined;
  export let unread: number | undefined = undefined;
  export let selfFetch: boolean = false;
  // 'button': icon + badge, used wherever a visible "Comments" affordance is wanted.
  // 'dot': badge only, and only rendered when there's something unread -- used on the compact
  // calendar card, which is already clickable to open the full activity dialog elsewhere.
  // 'toolbar': plain Bootstrap .btn.btn-default with an icon, "Comments" label, and count badge --
  // matches the legacy (non-Svelte) action-bar buttons alongside it (Share/Download/Delete etc. on
  // the activity details page), rather than the shadcn ghost-icon look the other variants use.
  export let variant: 'button' | 'dot' | 'toolbar' = 'button';
  // 'button' variant only: an outline button that says "Comments" beside the icon, instead of the icon alone.
  export let labelled: boolean = false;
  // Extra classes for the 'dot' variant's positioning -- callers overlaying it on their own card
  // layout (e.g. a corner badge) supply their own `absolute` placement here.
  let classNames: string = '';
  export { classNames as class };

  let resolvedTotal: number | undefined;
  let resolvedUnread: number | undefined;
  $: resolvedTotal = total;
  $: resolvedUnread = unread;

  async function selfFetchCounts() {
    try {
      const counts = await getActivityCommentCounts([activityPath]);
      const found = counts[activityPath];
      resolvedTotal = found?.total ?? 0;
      resolvedUnread = found?.unread ?? 0;
    } catch (e) {
      console.log(e);
    }
  }
  if (selfFetch) void selfFetchCounts();

  let open = false;
  function openDialog(e: MouseEvent) {
    e.stopPropagation();
    open = true;
  }

  // Clears this button's own badge immediately (works regardless of whether myFitness is
  // available), and also clears the shared commentCounts store entry so every other badge for
  // this same activity path (it can appear on more than one screen) updates too.
  function handleRead() {
    resolvedUnread = 0;
    myFitness?.markCommentsRead(activityPath);
  }
</script>

{#if variant === 'toolbar'}
  <button type="button" class="btn btn-default px-[9px] py-[3px] text-[13px] leading-normal" on:click={openDialog}>
    <i class="fa-regular fa-messages text-[13px]" data-toggle="tooltip" title="Comments"></i>
    Comments
    {#if (resolvedUnread ?? 0) > 0}
      <span class="badge badge-danger" style="margin-left: 4px;">{resolvedUnread}</span>
    {:else if (resolvedTotal ?? 0) > 0}
      <span class="badge badge-default" style="margin-left: 4px; background-color: #999;">{resolvedTotal}</span>
    {/if}
  </button>
{:else if variant === 'dot'}
  {#if (resolvedUnread ?? 0) > 0}
    <button
      type="button"
      title="{resolvedUnread} unread comment{resolvedUnread === 1 ? '' : 's'}"
      class={cn('absolute z-10 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground', classNames)}
      on:click={openDialog}
    >
      {resolvedUnread}
    </button>
  {/if}
{:else}
  <Button variant={labelled ? 'outline' : 'ghost'} size={labelled ? 'sm' : 'icon'} class="relative" title="Comments" on:click={openDialog}>
    {#if labelled}
      <i class="fa-light fa-messages text-xs mr-1" /> Comments
    {:else}
      <i class="fa-light fa-messages fa-lg" />
    {/if}
    {#if (resolvedUnread ?? 0) > 0}
      <span class="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
        {resolvedUnread}
      </span>
    {:else if (resolvedTotal ?? 0) > 0}
      <span class={cn('absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-medium text-muted-foreground border border-border')}>
        {resolvedTotal}
      </span>
    {/if}
  </Button>
{/if}

<Dialog.Root portal="#shadcn-ui-dialog-target" bind:open>
  <Dialog.Content class="max-w-lg max-h-[90dvh] flex flex-col">
    <Dialog.Header>
      <Dialog.Title>Comments{activityName ? ` — ${activityName}` : ''}</Dialog.Title>
    </Dialog.Header>
    {#if open}
      {#key activityPath}
        <div class='w-full overflow-auto'>
          <CommentThreadPanel
            load={(before) => getActivityComments(activityPath, before)}
            send={(text, files, references, replyToId) => postActivityComment(activityPath, text, files, references, replyToId)}
            markRead={() => markActivityCommentsRead(activityPath)}
            onRead={handleRead}
          />
        </div>
      {/key}
    {/if}
  </Dialog.Content>
</Dialog.Root>
